import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/* ════════════════════════════════════════════════════════════
   THEME — single source of truth for all colors.
   Change values here to re-skin the whole page later.
   ════════════════════════════════════════════════════════════ */
const THEME = {
  bg:            '#060b14',
  surface:       '#080f1a',
  surfaceAlt:    '#0d1526',
  border:        'rgba(255,255,255,0.08)',
  borderSoft:    'rgba(255,255,255,0.05)',

  primary:       '#6366f1',
  primaryHover:  '#4f46e5',
  primarySoft:   'rgba(99,102,241,0.12)',
  primaryBorder: 'rgba(99,102,241,0.3)',
  primaryText:   '#a5b4fc',

  success:       '#10b981',
  successText:   '#6ee7b7',
  successSoft:   'rgba(16,185,129,0.12)',
  successBorder: 'rgba(16,185,129,0.3)',

  warning:       '#f59e0b',
  warningText:   '#fbbf24',
  warningSoft:   'rgba(245,158,11,0.15)',
  warningBorder: 'rgba(245,158,11,0.35)',

  danger:        '#ef4444',
  dangerText:    '#fca5a5',
  dangerSoft:    'rgba(239,68,68,0.15)',
  dangerBorder:  'rgba(239,68,68,0.3)',

  textPrimary:   '#f1f5f9',
  textSecondary: '#94a3b8',
  textMuted:     '#64748b',
  textFaint:     '#475569',
  textGhost:     '#334155',

  fontDisplay: "'Space Grotesk', sans-serif",
  fontBody:    "'Inter', sans-serif",
};

/* ════════════════════════════════════════════════════════════
   STATIC DATA
   ════════════════════════════════════════════════════════════ */
const COUNTRIES = [
  { name: 'United Kingdom', flag: '🇬🇧' }, { name: 'Germany',      flag: '🇩🇪' },
  { name: 'United States',  flag: '🇺🇸' }, { name: 'Australia',    flag: '🇦🇺' },
  { name: 'Europe',         flag: '🇪🇺' }, { name: 'China',        flag: '🇨🇳' },
  { name: 'Sweden',         flag: '🇸🇪' }, { name: 'Turkey',       flag: '🇹🇷' },
  { name: 'Saudi Arabia',   flag: '🇸🇦' }, { name: 'Japan',        flag: '🇯🇵' },
  { name: 'Switzerland',    flag: '🇨🇭' }, { name: 'Hungary',      flag: '🇭🇺' },
  { name: 'Belgium',        flag: '🇧🇪' }, { name: 'South Korea',  flag: '🇰🇷' },
  { name: 'Netherlands',    flag: '🇳🇱' }, { name: 'France',       flag: '🇫🇷' },
  { name: 'USA',            flag: '🇺🇸' }, { name: 'Canada',       flag: '🇨🇦' },
  { name: 'Italy',          flag: '🇮🇹' }, { name: 'Spain',        flag: '🇪🇸' },
];

const FUNDING_TYPES = [{name:'Fully Funded'},{name:'Partially Funded'}];

const DEGREE_LEVELS = [
  { name: 'Bachelors' }, { name: 'Masters' }, { name: 'PhD' },
  { name: 'Bachelors/Masters' }, { name: 'Masters/PhD' }, { name: 'All' },
];

const IELTS_OPTIONS = [{ name: 'Yes' }, { name: 'No' }, { name: 'MOI Acceptable' }];
const TYPES         = [{ name: 'Government' }, { name: 'University' }, { name: 'Private' }];

const FUNDING_COLORS = {
  'Fully Funded':     { bg: THEME.successSoft, color: THEME.successText, border: THEME.successBorder },
  'Partially Funded': { bg: THEME.warningSoft, color: THEME.warningText, border: THEME.warningBorder },
  'Partial Funded':   { bg: THEME.warningSoft, color: THEME.warningText, border: THEME.warningBorder },
};

const DEGREE_COLORS = {
  'Bachelors':         { bg: 'rgba(245,158,11,0.18)', color: '#fcd34d', border: 'rgba(245,158,11,0.35)' },
  'Masters':           { bg: THEME.primarySoft,        color: THEME.primaryText, border: THEME.primaryBorder },
  'PhD':                { bg: 'rgba(168,85,247,0.18)',  color: '#d8b4fe', border: 'rgba(168,85,247,0.35)' },
  'Bachelors/Masters':  { bg: 'rgba(14,165,233,0.18)',  color: '#7dd3fc', border: 'rgba(14,165,233,0.35)' },
  'Masters/PhD':        { bg: 'rgba(236,72,153,0.18)',  color: '#f9a8d4', border: 'rgba(236,72,153,0.35)' },
  'All':                { bg: THEME.successSoft,        color: THEME.successText, border: THEME.successBorder },
};

const IELTS_COLORS = {
  'Yes':            { bg: THEME.dangerSoft,  color: THEME.dangerText,  border: THEME.dangerBorder },
  'No':             { bg: THEME.successSoft, color: THEME.successText, border: THEME.successBorder },
  'MOI Acceptable': { bg: THEME.warningSoft, color: THEME.warningText, border: THEME.warningBorder },
};

// const COUNTRY_COLORS = [
//   { bg: THEME.primarySoft,             color: THEME.primaryText, border: THEME.primaryBorder },
//   { bg: THEME.successSoft,             color: THEME.successText, border: THEME.successBorder },
//   { bg: THEME.warningSoft,             color: THEME.warningText, border: THEME.warningBorder },
//   { bg: THEME.dangerSoft,              color: THEME.dangerText,  border: THEME.dangerBorder  },
//   { bg: 'rgba(14,165,233,0.15)',       color: '#7dd3fc',         border: 'rgba(14,165,233,0.3)' },
//   { bg: 'rgba(168,85,247,0.15)',       color: '#d8b4fe',         border: 'rgba(168,85,247,0.3)' },
// ];

const ABBR_COLORS = [
  ['#4f46e5', '#eef2ff'], ['#0891b2', '#ecfeff'], ['#059669', '#ecfdf5'],
  ['#d97706', '#fffbeb'], ['#dc2626', '#fef2f2'], ['#7c3aed', '#f5f3ff'],
  ['#db2777', '#fdf2f8'], ['#0284c7', '#eff6ff'], ['#065f46', '#ecfdf5'],
];

const DEGREE_SHORT = {
  Bachelors: 'BA', Masters: 'MS', PhD: 'PhD',
  'Bachelors/Masters': 'BA/MS', 'Masters/PhD': 'MS/PhD', All: 'All',
};

const FLAG_MAP = {
  USA: '🇺🇸', 'United States': '🇺🇸', UK: '🇬🇧', 'United Kingdom': '🇬🇧',
  Germany: '🇩🇪', Australia: '🇦🇺', Canada: '🇨🇦', China: '🇨🇳',
  Japan: '🇯🇵', France: '🇫🇷', Netherlands: '🇳🇱', Sweden: '🇸🇪',
  Switzerland: '🇨🇭', Turkey: '🇹🇷', 'Saudi Arabia': '🇸🇦',
  'South Korea': '🇰🇷', Hungary: '🇭🇺', Belgium: '🇧🇪', Europe: '🇪🇺',
  Italy: '🇮🇹', Spain: '🇪🇸', Norway: '🇳🇴', Denmark: '🇩🇰',
  Finland: '🇫🇮', Ireland: '🇮🇪', Singapore: '🇸🇬', Pakistan: '🇵🇰',
  India: '🇮🇳', Austria: '🇦🇹', 'New Zealand': '🇳🇿', UAE: '🇦🇪',
  Qatar: '🇶🇦', Malaysia: '🇲🇾', Taiwan: '🇹🇼', Brazil: '🇧🇷',
};

/* ── TABLE COLUMNS ───────────────────────────────────────────
   IMPORTANT: every column except "name" gets a FIXED pixel width.
   The "name" column has no width (colWidth: null) which — inside a
   table-layout:fixed table with a <colgroup> — means it automatically
   absorbs 100% of whatever space is left over.

   This fixes BOTH bugs from the screenshots:
   1) At 100% zoom the Apply/Eligibility buttons were being squeezed
      into a slice that was too narrow, so they got visually clipped
      at the right edge. Fixed columns now always keep their full
      pixel width no matter the zoom level.
   2) At 50% zoom the long scholarship name ("Deutschlandstipendium -
      Technical University of Munich (TUM ...)") was overflowing
      instead of being cut off with "...". Because the name column now
      has a real, browser-computed width (not just a CSS percentage
      guess), the ellipsis truncation in the cell actually works.
   ──────────────────────────────────────────────────────────── */
const COLS = [
  { key: 'name',        label: 'SCHOLARSHIP', align: 'left',   colWidth: null   },
  { key: 'country',     label: 'COUNTRY',     align: 'center', colWidth: '110px' },
  { key: 'funding',     label: 'FUNDING',     align: 'center', colWidth: '150px' },
  { key: 'degree',      label: 'DEGREE',      align: 'center', colWidth: '100px' },
  { key: 'opening',     label: 'OPENING',     align: 'center', colWidth: '150px' },
  { key: 'deadline',    label: 'DEADLINE',    align: 'center', colWidth: '150px' },
  { key: 'ielts',       label: 'IELTS',       align: 'center', colWidth: '90px' },
  { key: 'eligibility', label: 'ELIGIBILITY', align: 'center', colWidth: '124px' },
  { key: 'apply',       label: 'APPLY',       align: 'center', colWidth: '116px' },
];
// Sum of every fixed column + a comfortable minimum for the name column.
const FIXED_COLS_WIDTH = COLS.reduce((sum, c) => sum + (c.colWidth ? parseInt(c.colWidth, 10) : 0), 0);
const MIN_NAME_WIDTH = 280;
const MIN_TABLE_WIDTH = FIXED_COLS_WIDTH + MIN_NAME_WIDTH;

/* ════════════════════════════════════════════════════════════
   HELPERS
   ════════════════════════════════════════════════════════════ */

// Fix broken encoding from non-UTF8 CSV
function fixStr(str) {
  if (!str || typeof str !== 'string') return str;
  return str
    .replace(/â‚¬/g, '€')
    .replace(/ðŸ‡©ðŸ‡ª/g, '🇩🇪')
    .replace(/ðŸ‡¬ðŸ‡§/g, '🇬🇧')
    .replace(/ðŸ‡ºðŸ‡¸/g, '🇺🇸')
    .replace(/â€™/g, "'")
    .replace(/â€œ/g, '"')
    .replace(/â€/g, '"')
    .replace(/Ã©/g, 'é')
    .replace(/Ã¼/g, 'ü')
    .replace(/Ã¶/g, 'ö')
    .replace(/Ã¤/g, 'ä')
    .trim();
}

function isUsefulBenefit(val) {
  if (!val) return false;
  const v = val.toLowerCase().trim();
  return !['', 'no', 'not covered', 'not applicable', 'not included', '-', 'n/a', 'tba'].includes(v);
}

// ✅ Rounds an exact count down to a clean round number for display,
//    e.g. 10345 → "10000+", 587 → "500+". Matches the "500K+" style
//    used on the Professors page.
function formatCount(n) {
  if (!n || n <= 0) return '0';
  if (n < 100) return `${n}`;
  const magnitude = 10 ** (String(Math.floor(n)).length - 1); // 1000 for 10345, 100 for 587
  return `${Math.floor(n / magnitude) * magnitude}+`;
}

function getFlag(s) {
  if (s.flag && s.flag.length > 1 && !s.flag.includes('ð')) return s.flag;
  return FLAG_MAP[s.country] || '🌍';
}

function getAbbr(s) {
  if (s.abbr) return s.abbr.replace(/-.*/, '').substring(0, 3).toUpperCase();
  return (s.name || 'SCH')
    .split(' ')
    .filter((w) => w.length > 1)
    .map((w) => w[0].toUpperCase())
    .join('')
    .substring(0, 3) || 'SCH';
}

function hashStr(str = '') {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffff;
  return h;
}

/* ════════════════════════════════════════════════════════════
   FILTER DROPDOWN
   ════════════════════════════════════════════════════════════ */
function FilterItem({ label, icon, options, isOpen, onToggle, selected, onSelect }) {
  const [query, setQuery] = useState('');
  const visible = options.filter((o) => o.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div
      style={{
        borderRadius: '10px',
        border: `1px solid ${isOpen ? THEME.primaryBorder : THEME.border}`,
        background: isOpen ? THEME.primarySoft : 'rgba(255,255,255,0.03)',
        overflow: 'hidden',
        transition: 'all 0.2s',
      }}
    >
      <button
        onClick={() => { onToggle(); setQuery(''); }}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '9px',
          padding: '11px 14px', background: 'transparent', border: 'none',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: '15px', flexShrink: 0 }}>{icon}</span>
        <span style={{
          flex: 1, fontFamily: THEME.fontBody, fontSize: '13px', fontWeight: '600',
          color: isOpen ? THEME.primaryText : THEME.textSecondary,
        }}>
          {label}
        </span>
        {selected && (
          <span style={{
            background: THEME.primary, color: '#fff', fontSize: '10px', fontWeight: '700',
            padding: '2px 8px', borderRadius: '20px',
          }}>
            1
          </span>
        )}
        <span style={{
          fontSize: '10px', color: THEME.textFaint,
          transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0,
        }}>
          ▼
        </span>
      </button>

      {isOpen && (
        <div style={{ padding: '0 10px 10px' }}>
          {options.length > 4 && (
            <div style={{ position: 'relative', marginBottom: '8px' }}>
              <span style={{
                position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)',
                fontSize: '11px', color: THEME.textFaint, pointerEvents: 'none',
              }}>
                🔍
              </span>
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                style={{
                  width: '100%', padding: '7px 8px 7px 26px', background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: THEME.textPrimary,
                  fontFamily: THEME.fontBody, fontSize: '12px', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
          )}
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {visible.map((opt) => {
              const active = selected === opt.name;
              return (
                <div
                  key={opt.name}
                  onClick={() => onSelect(active ? '' : opt.name)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 8px',
                    borderRadius: '7px', cursor: 'pointer',
                    background: active ? THEME.primarySoft : 'transparent',
                    border: `1px solid ${active ? THEME.primaryBorder : 'transparent'}`,
                    marginBottom: '2px', transition: 'all 0.12s',
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  <input
                    type="checkbox" readOnly checked={active}
                    style={{ width: '12px', height: '12px', accentColor: THEME.primary, cursor: 'pointer', flexShrink: 0 }}
                  />
                  {opt.flag && <span style={{ fontSize: '13px' }}>{opt.flag}</span>}
                  <span style={{
                    flex: 1, fontFamily: THEME.fontBody, fontSize: '12px',
                    color: active ? THEME.primaryText : THEME.textSecondary,
                    fontWeight: active ? '600' : '400',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {opt.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   ELIGIBILITY MODAL
   ════════════════════════════════════════════════════════════ */
function EligibilityModal({ s, onClose }) {
  const flag = getFlag(s);

  // ✅ Flat field access — backend's short-key schema returns these
  //    directly on `s` (no more nested s.eligibility.benefits.*)
  const benefitItems = [
    { label: 'Monthly Stipend', icon: '💵', value: s.stipend },
    { label: 'Tuition Fee',     icon: '🎓', value: s.tuition },
    { label: 'Accommodation',   icon: '🏠', value: s.accommodation },
    { label: 'Meals',           icon: '🍽️', value: s.meals },
    { label: 'Airfare/Travel',  icon: '✈️', value: s.airfare },
    { label: 'Other Benefits',  icon: '🎁', value: s.other },
  ].filter((item) => isUsefulBenefit(item.value));

  const eligItems = [
    { label: 'Work Experience',  icon: '💼', value: fixStr(s.workExp  || 'Not Required') },
    { label: 'Age Limit',        icon: '🎂', value: fixStr(s.ageLimit || 'No limit') },
    { label: 'Program Duration', icon: '⏳', value: fixStr(s.duration || 'TBA') },
    { label: 'IELTS Required',   icon: '📝', value: fixStr(s.ielts    || 'Check website') },
  ];

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: THEME.surfaceAlt, borderRadius: '22px', padding: '32px', width: '100%',
          maxWidth: '580px', border: `1px solid ${THEME.border}`, maxHeight: '92vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '20px' }}>
          <div style={{
            width: '54px', height: '54px', borderRadius: '14px', background: THEME.primarySoft,
            border: `1px solid ${THEME.primaryBorder}`, display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '28px', flexShrink: 0,
          }}>
            {flag}
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <h3 style={{
              fontFamily: THEME.fontDisplay, fontSize: '16px', fontWeight: '700',
              color: THEME.textPrimary, margin: '0 0 6px', lineHeight: '1.4',
            }}>
              {fixStr(s.name)}
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              <span style={{ fontFamily: THEME.fontBody, fontSize: '11px', color: THEME.textMuted }}>📍 {fixStr(s.country)}</span>
              <span style={{ fontFamily: THEME.fontBody, fontSize: '11px', color: THEME.textFaint }}>·</span>
              <span style={{ fontFamily: THEME.fontBody, fontSize: '11px', color: THEME.textMuted }}>🏛️ {fixStr(s.type) || 'Scholarship'}</span>
              {s.host && (
                <>
                  <span style={{ fontFamily: THEME.fontBody, fontSize: '11px', color: THEME.textFaint }}>·</span>
                  <span style={{ fontFamily: THEME.fontBody, fontSize: '11px', color: THEME.textMuted }}>{fixStr(s.host)}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {s.description && (
          <div style={{
            background: 'rgba(255,255,255,0.03)', borderRadius: '10px', padding: '12px 14px',
            marginBottom: '16px', border: `1px solid ${THEME.borderSoft}`,
          }}>
            <p style={{ fontFamily: THEME.fontBody, fontSize: '13px', color: THEME.textSecondary, margin: 0, lineHeight: '1.7' }}>
              {fixStr(s.description)}
            </p>
          </div>
        )}

        {/* Field of Study */}
        {s.field && s.field !== 'All Fields' && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: '8px', background: THEME.primarySoft,
            borderRadius: '10px', padding: '10px 14px', marginBottom: '16px', border: '1px solid rgba(99,102,241,0.15)',
          }}>
            <span style={{ fontSize: '14px', flexShrink: 0, marginTop: '1px' }}>📚</span>
            <div>
              <div style={{ fontFamily: THEME.fontBody, fontSize: '10px', color: THEME.textFaint, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Field of Study
              </div>
              <div style={{ fontFamily: THEME.fontBody, fontSize: '12px', fontWeight: '600', color: THEME.primaryText, lineHeight: '1.5' }}>
                {fixStr(s.field)}
              </div>
            </div>
          </div>
        )}

        {/* Dates */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
          <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '10px', padding: '12px', border: `1px solid ${THEME.borderSoft}` }}>
            <div style={{ fontFamily: THEME.fontBody, fontSize: '10px', color: THEME.textFaint, marginBottom: '4px' }}>📅 App Opening</div>
            <div style={{ fontFamily: THEME.fontBody, fontSize: '13px', fontWeight: '600', color: THEME.textPrimary }}>{fixStr(s.appOpening) || 'TBA'}</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '10px', padding: '12px', border: '1px solid rgba(239,68,68,0.15)' }}>
            <div style={{ fontFamily: THEME.fontBody, fontSize: '10px', color: THEME.textFaint, marginBottom: '4px' }}>⏰ Deadline</div>
            <div style={{ fontFamily: THEME.fontBody, fontSize: '13px', fontWeight: '600', color: THEME.warning }}>{fixStr(s.deadline) || 'TBA'}</div>
          </div>
        </div>

        {/* Eligibility Grid */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontFamily: THEME.fontBody, fontSize: '11px', fontWeight: '700', color: THEME.textFaint, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
            ✅ Eligibility Requirements
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {eligItems.map((item) => (
              <div key={item.label} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '10px', padding: '11px 12px', border: `1px solid ${THEME.borderSoft}` }}>
                <div style={{ fontFamily: THEME.fontBody, fontSize: '10px', color: THEME.textFaint, marginBottom: '4px' }}>{item.icon} {item.label}</div>
                <div style={{ fontFamily: THEME.fontBody, fontSize: '12px', fontWeight: '600', color: THEME.textPrimary, lineHeight: '1.4' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits Package */}
        <div style={{ background: THEME.successSoft, borderRadius: '12px', padding: '16px', border: `1px solid ${THEME.successBorder}`, marginBottom: '20px' }}>
          <div style={{ fontFamily: THEME.fontBody, fontSize: '11px', fontWeight: '700', color: THEME.success, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            💰 Benefits Package
          </div>
          {benefitItems.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {benefitItems.map((bi) => (
                <div key={bi.label} style={{
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px',
                  fontFamily: THEME.fontBody, fontSize: '13px', padding: '6px 0', borderBottom: `1px solid ${THEME.borderSoft}`,
                }}>
                  <span style={{ color: THEME.textMuted, flexShrink: 0, whiteSpace: 'nowrap' }}>{bi.icon} {bi.label}</span>
                  <span style={{ color: THEME.textPrimary, fontWeight: '500', textAlign: 'right', lineHeight: '1.5' }}>{fixStr(bi.value)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontFamily: THEME.fontBody, fontSize: '13px', color: THEME.textFaint, margin: 0, lineHeight: '1.6' }}>
              Visit the official website for complete benefits and funding details.
            </p>
          )}
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <a
            href={s.link || '#'} target="_blank" rel="noreferrer"
            style={{
              flex: 1, padding: '13px', background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.primaryHover})`,
              borderRadius: '10px', color: '#fff', fontFamily: THEME.fontBody, fontSize: '14px', fontWeight: '700',
              textAlign: 'center', textDecoration: 'none', display: 'block', transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.85')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            Apply Now →
          </a>
          <button
            onClick={onClose}
            style={{
              padding: '13px 22px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${THEME.border}`,
              borderRadius: '10px', color: THEME.textMuted, fontFamily: THEME.fontBody, fontSize: '14px', cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════ */
export default function ScholarshipPage() {
  const [scholarships, setScholarships] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detailSchol, setDetailSchol] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedFunding, setSelectedFunding] = useState('');
  const [selectedDegree, setSelectedDegree] = useState('');
  const [selectedIelts, setSelectedIelts] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [openFilter, setOpenFilter] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer toggle
  const LIMIT = 15;

  const fetchScholarships = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      // ✅ page size is hardcoded server-side (15) — no need to send limit
      const params = { page: pg };
      if (selectedCountry) params.country      = selectedCountry;
      if (selectedFunding) params.fundingFilter = selectedFunding;
      if (selectedDegree)  params.degreeLevel   = selectedDegree;
      if (selectedIelts)   params.ieltsFilter   = selectedIelts;
      if (selectedType)    params.type          = selectedType;
      const res = await axios.get(`${API}/scholarships`, { params });
      setScholarships(res.data.results || []);
      setTotal(res.data.total || 0);
      setPage(pg);
    } catch {
      toast.error('Could not load scholarships');
    }
    setLoading(false);
  }, [selectedCountry, selectedFunding, selectedDegree, selectedIelts, selectedType]);

  useEffect(() => { fetchScholarships(1); }, [fetchScholarships]);

  const clearAll = () => {
    setSelectedCountry(''); setSelectedFunding(''); setSelectedDegree('');
    setSelectedIelts(''); setSelectedType(''); setOpenFilter(null);
  };
  const togFilter = (id) => setOpenFilter((prev) => (prev === id ? null : id));
  const activeCount = [selectedCountry, selectedFunding, selectedDegree, selectedIelts, selectedType].filter(Boolean).length;
  const totalPages = Math.ceil(total / LIMIT);

  const activeTags = [
    selectedCountry && { key: 'country', label: selectedCountry, clear: () => setSelectedCountry('') },
    selectedFunding && { key: 'funding', label: selectedFunding, clear: () => setSelectedFunding('') },
    selectedDegree && { key: 'degree', label: selectedDegree, clear: () => setSelectedDegree('') },
    selectedIelts && { key: 'ielts', label: `IELTS: ${selectedIelts}`, clear: () => setSelectedIelts('') },
    selectedType && { key: 'type', label: selectedType, clear: () => setSelectedType('') },
  ].filter(Boolean);

  const pill = (text, style, maxW = '120px') => (
    <span
      title={fixStr(text)}
      style={{
        fontFamily: THEME.fontBody, fontSize: '11px', fontWeight: '500', background: style.bg,
        color: style.color, border: `1px solid ${style.border}`, padding: '3px 8px', borderRadius: '20px',
        display: 'inline-block', maxWidth: maxW, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}
    >
      {fixStr(text)}
    </span>
  );

  const FilterPanel = (
    <>
      <div style={{ padding: '14px 14px 10px', borderBottom: `1px solid ${THEME.borderSoft}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '10px', fontWeight: '700', color: THEME.textFaint, textTransform: 'uppercase', letterSpacing: '1.2px' }}>
            FILTERS
          </span>
          {activeCount > 0 && (
            <button onClick={clearAll} style={{ fontSize: '11px', color: THEME.danger, background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: '600' }}>
              Clear all ({activeCount})
            </button>
          )}
        </div>
      </div>
      <div style={{ padding: '8px 10px', flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <FilterItem label="Country" icon="🌍" options={COUNTRIES} isOpen={openFilter === 'country'} onToggle={() => togFilter('country')} selected={selectedCountry} onSelect={(v) => { setSelectedCountry(v); togFilter('country'); }} />
        <FilterItem label="Funding" icon="💰" options={FUNDING_TYPES} isOpen={openFilter === 'funding'} onToggle={() => togFilter('funding')} selected={selectedFunding} onSelect={(v) => { setSelectedFunding(v); togFilter('funding'); }} />
        <FilterItem label="Degree" icon="🎓" options={DEGREE_LEVELS} isOpen={openFilter === 'degree'} onToggle={() => togFilter('degree')} selected={selectedDegree} onSelect={(v) => { setSelectedDegree(v); togFilter('degree'); }} />
        <FilterItem label="IELTS" icon="📝" options={IELTS_OPTIONS} isOpen={openFilter === 'ielts'} onToggle={() => togFilter('ielts')} selected={selectedIelts} onSelect={(v) => { setSelectedIelts(v); togFilter('ielts'); }} />
        <FilterItem label="Type" icon="🏛️" options={TYPES} isOpen={openFilter === 'type'} onToggle={() => togFilter('type')} selected={selectedType} onSelect={(v) => { setSelectedType(v); togFilter('type'); }} />
      </div>
      {activeTags.length > 0 && (
        <div style={{ padding: '10px 12px', borderTop: `1px solid ${THEME.borderSoft}`, display: 'flex', flexWrap: 'wrap', gap: '5px', flexShrink: 0 }}>
          {activeTags.map((tag) => (
            <span
              key={tag.key} onClick={tag.clear}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px',
                background: THEME.primarySoft, color: THEME.primaryText, border: `1px solid ${THEME.primaryBorder}`,
                padding: '3px 9px', borderRadius: '20px', cursor: 'pointer',
              }}
            >
              {tag.label} ✕
            </span>
          ))}
        </div>
      )}
    </>
  );

  return (
    <div style={{ display: 'flex', minHeight: 'calc(100vh - 68px)', background: THEME.bg, fontFamily: THEME.fontBody }}>

      {/* ── SIDEBAR (desktop) ── */}
      <div
        className="scholarship-sidebar"
        style={{
          width: '240px', flexShrink: 0, background: THEME.surface, borderRight: `1px solid ${THEME.border}`,
          position: 'sticky', top: '68px', height: 'calc(100vh - 68px)', overflowY: 'auto',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {FilterPanel}
      </div>

      {/* ── MAIN ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

        {/* ── TAGLINE BAR ── */}
        <div style={{
          padding: '10px 20px', borderBottom: `1px solid ${THEME.borderSoft}`, background: THEME.surface,
          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
          minHeight: '52px', flexWrap: 'wrap',
        }}>
          <button
            className="scholarship-filter-toggle"
            onClick={() => setSidebarOpen(true)}
            style={{
              display: 'none', padding: '7px 12px', background: THEME.primarySoft, border: `1px solid ${THEME.primaryBorder}`,
              borderRadius: '8px', color: THEME.primaryText, fontFamily: THEME.fontBody, fontSize: '12px',
              fontWeight: '600', cursor: 'pointer',
            }}
          >
            ☰ Filters {activeCount > 0 ? `(${activeCount})` : ''}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap', overflowX: 'auto' }}>
            {[
              { n: '1', label: 'Pick Country & Degree' },
              { n: '2', label: 'Check Eligibility' },
              { n: '3', label: 'Apply Before Deadline' },
            ].map((step, i, arr) => (
              <div key={step.n} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px', background: THEME.primarySoft,
                  border: `1px solid ${THEME.primaryBorder}`, borderRadius: '9px', padding: '7px 15px', whiteSpace: 'nowrap',
                }}>
                  <div style={{
                    width: '22px', height: '22px', borderRadius: '50%', background: THEME.primary, color: '#fff',
                    fontSize: '12px', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {step.n}
                  </div>
                  <span style={{ fontFamily: THEME.fontBody, fontSize: '13px', fontWeight: '700', color: '#dde4ff' }}>{step.label}</span>
                </div>
                {i < arr.length - 1 && <span style={{ color: THEME.primaryHover, fontSize: '16px', fontWeight: '900' }}>→</span>}
              </div>
            ))}
            <span style={{ color: THEME.primaryHover, fontSize: '16px', fontWeight: '900' }}>→</span>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px', background: THEME.successSoft,
              border: `1px solid ${THEME.successBorder}`, borderRadius: '9px', padding: '7px 15px',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              <div style={{
                width: '8px', height: '8px', borderRadius: '50%', background: THEME.success, flexShrink: 0,
                boxShadow: '0 0 0 3px rgba(16,185,129,0.20)',
              }} />
              <span style={{ fontFamily: THEME.fontBody, fontSize: '13px', fontWeight: '800', color: THEME.success }}>Get Fully Funded!</span>
            </div>
          </div>

          <div style={{
            fontSize: '12px', color: THEME.textFaint, padding: '5px 12px', borderRadius: '8px',
            border: `1px solid ${THEME.border}`, background: 'rgba(255,255,255,0.03)', whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            <strong style={{ color: THEME.primaryText }}>{total ? formatCount(total) : '500+'}</strong> scholarships
          </div>
        </div>

        {/* ── STATS ── */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px',
          padding: '14px 20px', borderBottom: `1px solid ${THEME.borderSoft}`, background: THEME.surface, flexShrink: 0,
        }}>
          {[
            { val: total ? formatCount(total) : '500+', lbl: 'Scholarships', icon: '🏆', color: THEME.primaryText },
            { val: '150+', lbl: 'Countries', icon: '🌍', color: THEME.successText },
            { val: 'Mixed', lbl: 'Funding Types', icon: '💰', color: THEME.warningText },
            { val: scholarships.length, lbl: 'Showing Now', icon: '📋', color: '#60a5fa' },
          ].map((s, i) => (
            <div key={i} style={{
              textAlign: 'center', padding: '14px 8px', background: 'rgba(255,255,255,0.03)',
              border: `1px solid ${THEME.border}`, borderRadius: '12px', display: 'flex',
              flexDirection: 'column', alignItems: 'center', gap: '5px',
            }}>
              <span style={{ fontSize: '20px' }}>{s.icon}</span>
              <div style={{ fontSize: '20px', fontWeight: '700', color: s.color, lineHeight: 1 }}>{s.val}</div>
              <div style={{ fontSize: '10px', color: THEME.textFaint, letterSpacing: '0.3px' }}>{s.lbl}</div>
            </div>
          ))}
        </div>

        {/* ── TABLE ── */}
        <div style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', paddingRight: '4px' }}>
          <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%', minWidth: `${MIN_TABLE_WIDTH}px` }}>
            {/* colgroup is what makes table-layout:fixed truncate the name
                column reliably and keep every action column full-width */}
            <colgroup>
              {COLS.map((c) => (
                <col key={c.key} style={c.colWidth ? { width: c.colWidth } : undefined} />
              ))}
            </colgroup>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: THEME.surfaceAlt }}>
              <tr style={{ borderBottom: `1px solid ${THEME.border}` }}>
                {COLS.map((h) => (
                  <th
                    key={h.key}
                    style={{
                      padding: '10px 12px', textAlign: h.align,
                      fontFamily: THEME.fontBody, fontSize: '10px', fontWeight: '700', color: THEME.textGhost,
                      textTransform: 'uppercase', letterSpacing: '0.8px', whiteSpace: 'nowrap',
                    }}
                  >
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Loading skeleton */}
              {loading && Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${THEME.borderSoft}` }}>
                  {COLS.map((c) => (
                    <td key={c.key} style={{ padding: '12px' }}>
                      <div style={{ height: '12px', borderRadius: '4px', background: 'rgba(255,255,255,0.05)', width: '70%', margin: '0 auto' }} />
                    </td>
                  ))}
                </tr>
              ))}

              {/* Rows */}
              {!loading && scholarships.map((s) => {
                const abbrColors = ABBR_COLORS[hashStr(s.name) % ABBR_COLORS.length];
                // ✅ Use the normalized filter-fields for colors/pills — fundingType/
                //    degree/ielts are now raw free-text detail fields, while
                //    fundingFilter/degreeLevel/ieltsFilter are the fixed enums.
                const fundStyle = FUNDING_COLORS[s.fundingFilter] || { bg: 'rgba(71,85,105,0.15)', color: THEME.textMuted, border: 'rgba(71,85,105,0.3)' };
                const degreeStyle = DEGREE_COLORS[s.degreeLevel] || DEGREE_COLORS['All'];
                const ieltsStyle = IELTS_COLORS[s.ieltsFilter] || IELTS_COLORS['Yes'];
                const abbrText = getAbbr(s);
                const degreeShort = DEGREE_SHORT[s.degreeLevel] || (s.degreeLevel?.substring(0, 5) || '—');
                const flag = getFlag(s);
                const td = { padding: '11px 12px', verticalAlign: 'middle' };

                return (
                  <tr
                    key={s._id}
                    style={{ borderBottom: `1px solid ${THEME.borderSoft}`, transition: 'background 0.12s' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = THEME.primarySoft)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    {/* SCHOLARSHIP NAME — the div below has overflow:hidden + a
                        real computed width from colgroup, so long names always
                        truncate with "..." instead of overflowing the row */}
                    <td style={{ ...td, textAlign: 'left' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '10px', background: abbrColors[0],
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          <span style={{ fontFamily: THEME.fontDisplay, fontSize: '11px', fontWeight: '800', color: abbrColors[1], letterSpacing: '0.5px' }}>
                            {abbrText}
                          </span>
                        </div>
                        <div style={{ overflow: 'hidden', flex: '1 1 0%', minWidth: 0 }}>
                          <div title={fixStr(s.name)} style={{ fontFamily: THEME.fontBody, fontSize: '13px', fontWeight: '600', color: THEME.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {fixStr(s.name)}
                          </div>
                          <div style={{ fontFamily: THEME.fontBody, fontSize: '11px', color: THEME.textFaint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '2px' }}>
                            {fixStr(s.host)}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* COUNTRY */}
                    <td style={{ ...td, textAlign: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                        <span style={{ fontSize: '16px' }}>{flag}</span>
                        <span style={{ fontFamily: THEME.fontBody, fontSize: '10px', color: THEME.textMuted, maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.country}>
                          {s.country}
                        </span>
                      </div>
                    </td>

                    {/* FUNDING */}
                    <td style={{ ...td, textAlign: 'center' }}>{pill(s.fundingFilter, fundStyle, '124px')}</td>

                    {/* DEGREE */}
                    <td style={{ ...td, textAlign: 'center' }}>
                      <span title={s.degreeLevel} style={{
                        fontFamily: THEME.fontBody, fontSize: '11px', fontWeight: '600', background: degreeStyle.bg,
                        color: degreeStyle.color, border: `1px solid ${degreeStyle.border}`, padding: '3px 10px',
                        borderRadius: '20px', display: 'inline-block', whiteSpace: 'nowrap',
                      }}>
                        {degreeShort}
                      </span>
                    </td>

                    {/* OPENING */}
                    <td title={fixStr(s.appOpening) || 'TBA'} style={{ ...td, fontFamily: THEME.fontBody, fontSize: '12px', color: THEME.textSecondary, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      📅 {fixStr(s.appOpening) || 'TBA'}
                    </td>

                    {/* DEADLINE */}
                    <td title={fixStr(s.deadline) || 'TBA'} style={{ ...td, fontFamily: THEME.fontBody, fontSize: '12px', color: THEME.warning, fontWeight: '500', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      ⏰ {fixStr(s.deadline) || 'TBA'}
                    </td>

                    {/* IELTS */}
                    <td style={{ ...td, textAlign: 'center' }}>{pill(s.ieltsFilter || 'Yes', ieltsStyle, '76px')}</td>

                    {/* ELIGIBILITY BUTTON */}
                    <td style={{ ...td, textAlign: 'center' }}>
                      <button
                        onClick={() => setDetailSchol(s)}
                        style={{
                          padding: '6px 12px', background: THEME.primarySoft, border: `1px solid ${THEME.primaryBorder}`,
                          borderRadius: '8px', color: THEME.primaryText, fontFamily: THEME.fontBody, fontSize: '12px',
                          fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(99,102,241,0.25)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = THEME.primarySoft)}
                      >
                        View Details
                      </button>
                    </td>

                    {/* APPLY */}
                    <td style={{ ...td, textAlign: 'center' }}>
                      <a
                        href={s.link || '#'} target="_blank" rel="noreferrer"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '7px 14px',
                          background: THEME.primary, borderRadius: '8px', color: '#fff', fontFamily: THEME.fontBody,
                          fontSize: '12px', fontWeight: '600', textDecoration: 'none', whiteSpace: 'nowrap', transition: 'background 0.15s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = THEME.primaryHover)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = THEME.primary)}
                      >
                        Apply →
                      </a>
                    </td>
                  </tr>
                );
              })}

              {/* Empty */}
              {!loading && scholarships.length === 0 && (
                <tr>
                  <td colSpan={COLS.length} style={{ textAlign: 'center', padding: '70px 0' }}>
                    <div style={{ fontSize: '44px', marginBottom: '14px' }}>🎓</div>
                    <div style={{ fontFamily: THEME.fontBody, fontSize: '15px', color: THEME.textMuted, marginBottom: '8px' }}>No scholarships found</div>
                    <div style={{ fontFamily: THEME.fontBody, fontSize: '12px', color: THEME.textGhost }}>Try adjusting your filters</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── PAGINATION ── */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px',
            borderTop: `1px solid ${THEME.borderSoft}`, background: THEME.surface, flexShrink: 0, flexWrap: 'wrap', gap: '8px',
          }}>
            <span style={{ fontFamily: THEME.fontBody, fontSize: '11px', color: THEME.textGhost }}>
              Showing {((page - 1) * LIMIT + 1)}–{Math.min(page * LIMIT, total)} of {total}
            </span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                onClick={() => fetchScholarships(page - 1)} disabled={page === 1}
                style={{
                  padding: '5px 12px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${THEME.border}`,
                  borderRadius: '6px', color: page === 1 ? THEME.textGhost : THEME.textSecondary,
                  cursor: page === 1 ? 'not-allowed' : 'pointer', fontFamily: THEME.fontBody, fontSize: '12px',
                  opacity: page === 1 ? 0.4 : 1,
                }}
              >
                ← Prev
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pg = page <= 3 ? i + 1 : page - 2 + i;
                if (pg < 1 || pg > totalPages) return null;
                return (
                  <button
                    key={pg} onClick={() => fetchScholarships(pg)}
                    style={{
                      padding: '5px 11px', background: pg === page ? THEME.primary : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${pg === page ? THEME.primary : THEME.border}`, borderRadius: '6px',
                      color: pg === page ? '#fff' : THEME.textSecondary, cursor: 'pointer',
                      fontFamily: THEME.fontBody, fontSize: '12px', fontWeight: pg === page ? '700' : '400',
                    }}
                  >
                    {pg}
                  </button>
                );
              })}
              <button
                onClick={() => fetchScholarships(page + 1)} disabled={page === totalPages}
                style={{
                  padding: '5px 12px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${THEME.border}`,
                  borderRadius: '6px', color: page === totalPages ? THEME.textGhost : THEME.textSecondary,
                  cursor: page === totalPages ? 'not-allowed' : 'pointer', fontFamily: THEME.fontBody, fontSize: '12px',
                  opacity: page === totalPages ? 0.4 : 1,
                }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── MOBILE FILTER DRAWER ── */}
      {sidebarOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 900 }}
          onClick={() => setSidebarOpen(false)}
        >
          <div
            style={{
              position: 'absolute', left: 0, top: 0, bottom: 0, width: '82%', maxWidth: '300px',
              background: THEME.surface, display: 'flex', flexDirection: 'column', overflowY: 'auto',
              borderRight: `1px solid ${THEME.border}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px' }}>
              <button
                onClick={() => setSidebarOpen(false)}
                style={{ background: 'transparent', border: 'none', color: THEME.textSecondary, fontSize: '18px', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
            {FilterPanel}
          </div>
        </div>
      )}

      {/* Modal */}
      {detailSchol && <EligibilityModal s={detailSchol} onClose={() => setDetailSchol(null)} />}

      {/* Responsive rules: collapse the fixed sidebar into a drawer on narrow screens */}
      <style>{`
        @media (max-width: 900px) {
          .scholarship-sidebar { display: none; }
          .scholarship-filter-toggle { display: inline-flex !important; }
        }
      `}</style>
    </div>
  );
}